import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const VendorInfoTab = ({ vendor, onVendorUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [loadingVendorTypes, setLoadingVendorTypes] = useState(true);
  const [formData, setFormData] = useState({
    name: vendor?.name || '',
    dba: vendor?.dba || '',
    vendor_type_id: vendor?.vendor_type_id || '',
    description: vendor?.description || '',
    website: vendor?.website || '',
    phone: vendor?.phone || '',
    address_line1: vendor?.address_line1 || '',
    address_line2: vendor?.address_line2 || '',
    city: vendor?.city || '',
    state: vendor?.state || '',
    zip_code: vendor?.zip_code || '',
    notes: vendor?.notes || '',
    is_active: vendor?.is_active !== undefined ? vendor.is_active : true,
    is_preferred: vendor?.is_preferred !== undefined ? vendor.is_preferred : false
  });

  // Fetch vendor types on component mount
  useEffect(() => {
    fetchVendorTypes();
  }, []);

  const fetchVendorTypes = async () => {
    try {
      const response = await api.get('/settings/vendor-types');
      // Only show active vendor types
      const activeTypes = (response.data.vendorTypes || []).filter(type => type.is_active);
      setVendorTypes(activeTypes);
    } catch (error) {
      console.error('Error fetching vendor types:', error);
    } finally {
      setLoadingVendorTypes(false);
    }
  };

  // Update formData when vendor prop changes
  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        dba: vendor.dba || '',
        vendor_type_id: vendor.vendor_type_id || '',
        description: vendor.description || '',
        website: vendor.website || '',
        phone: vendor.phone || '',
        address_line1: vendor.address_line1 || '',
        address_line2: vendor.address_line2 || '',
        city: vendor.city || '',
        state: vendor.state || '',
        zip_code: vendor.zip_code || '',
        notes: vendor.notes || '',
        is_active: vendor.is_active !== undefined ? vendor.is_active : true,
        is_preferred: vendor.is_preferred !== undefined ? vendor.is_preferred : false
      });
    }
  }, [vendor]);

  // Find the current vendor type name for display
  const currentVendorType = vendorTypes.find(type => type.id === vendor?.vendor_type_id);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      console.log('Sending update request with data:', formData);
      
      const response = await api.put(`/vendors/${vendor.id}`, formData);
      
      console.log('Update response:', response.data);
      
      // Update the vendor state with the response data
      if (response.data) {
        onVendorUpdate(response.data);
        setIsEditing(false);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error updating vendor:', err);
      setError('Failed to update vendor. Please try again. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle vendor active status
  const toggleActiveStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newStatus = !vendor.is_active;
      
      const response = await api.patch(`/vendors/${vendor.id}/toggle-active`, {
        is_active: newStatus
      });
      
      if (response.data) {
        onVendorUpdate(response.data);
        setFormData({
          ...formData,
          is_active: response.data.is_active
        });
      }
    } catch (err) {
      console.error('Error toggling vendor active status:', err);
      setError('Failed to update vendor status. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle vendor preferred status
  const togglePreferredStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newStatus = !vendor.is_preferred;
      
      const response = await api.patch(`/vendors/${vendor.id}/toggle-preferred`, {
        is_preferred: newStatus
      });
      
      if (response.data) {
        onVendorUpdate(response.data);
        setFormData({
          ...formData,
          is_preferred: response.data.is_preferred
        });
      }
    } catch (err) {
      console.error('Error toggling vendor preferred status:', err);
      setError('Failed to update vendor preferred status. ' + 
               (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleEditMode = () => {
    if (isEditing) {
      // Reset form data to original vendor data if canceling edit
      setFormData({
        name: vendor.name || '',
        dba: vendor.dba || '',
        vendor_type_id: vendor.vendor_type_id || '',
        description: vendor.description || '',
        website: vendor.website || '',
        phone: vendor.phone || '',
        address_line1: vendor.address_line1 || '',
        address_line2: vendor.address_line2 || '',
        city: vendor.city || '',
        state: vendor.state || '',
        zip_code: vendor.zip_code || '',
        notes: vendor.notes || '',
        is_active: vendor.is_active !== undefined ? vendor.is_active : true,
        is_preferred: vendor.is_preferred !== undefined ? vendor.is_preferred : false
      });
      setError(null);
    }
    setIsEditing(!isEditing);
  };

  if (!vendor) {
    return <div>Loading vendor information...</div>;
  }

  return (
    <div className="mt-6 space-y-6">
      {error && (
        <div className="p-4 text-red-700 bg-red-50 rounded-md border-l-4 border-red-500">
          <p>{error}</p>
        </div>
      )}
      
      {/* Header with Edit Controls - Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-indigo-50">
          <div>
            <h3 className="text-lg leading-6 font-medium text-indigo-900">Vendor Information</h3>
            <p className="mt-1 max-w-2xl text-sm text-indigo-700">Manage all vendor details and settings.</p>
          </div>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={toggleEditMode}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={toggleEditMode}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={toggleActiveStatus}
                  disabled={loading}
                  className={`inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm 
                    ${vendor.is_active 
                      ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100'} 
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {vendor.is_active ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  type="button"
                  onClick={togglePreferredStatus}
                  disabled={loading}
                  className={`inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded shadow-sm 
                    ${vendor.is_preferred 
                      ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}
                    ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {vendor.is_preferred ? 'Remove Preferred' : 'Mark Preferred'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Vendor Information Content - Conditional Rendering for Edit/View Mode */}
        <div className="px-4 py-5 sm:p-6">
          {isEditing ? (
            /* Edit Mode - Form Fields */
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                {/* Basic Information */}
                <div className="sm:col-span-2 border-b border-indigo-100 pb-4">
                  <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Basic Information</h4>
                </div>
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Vendor Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="dba" className="block text-sm font-medium text-gray-700">
                    DBA (Doing Business As)
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="dba"
                      id="dba"
                      value={formData.dba}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="vendor_type_id" className="block text-sm font-medium text-gray-700">
                    Vendor Type
                  </label>
                  <div className="mt-1">
                    <select
                      name="vendor_type_id"
                      id="vendor_type_id"
                      value={formData.vendor_type_id || ''}
                      onChange={(e) => setFormData({ ...formData, vendor_type_id: e.target.value || null })}
                      disabled={loading || loadingVendorTypes}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:opacity-50"
                    >
                      <option value="">Select a vendor type...</option>
                      {vendorTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <div className="mt-1">
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <div className="mt-1">
                    <input
                      type="url"
                      name="website"
                      id="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Brief description of the vendor and their services..."
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="sm:col-span-2 border-b border-indigo-100 pb-4 mt-6">
                  <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Address Information</h4>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700">
                    Address Line 1
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="address_line1"
                      id="address_line1"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700">
                    Address Line 2
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="address_line2"
                      id="address_line2"
                      value={formData.address_line2}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="city"
                      id="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="state"
                      id="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="zip_code" className="block text-sm font-medium text-gray-700">
                    ZIP Code
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="zip_code"
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="sm:col-span-2 border-b border-indigo-100 pb-4 mt-6">
                  <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Additional Information</h4>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <div className="mt-1">
                    <textarea
                      name="notes"
                      id="notes"
                      rows={4}
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Any additional notes about this vendor..."
                    />
                  </div>
                </div>

                <div className="sm:col-span-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      name="is_active"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Active vendor
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="is_preferred"
                      name="is_preferred"
                      type="checkbox"
                      checked={formData.is_preferred}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_preferred" className="ml-2 block text-sm text-gray-900">
                      Preferred vendor
                    </label>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            /* View Mode - Read-only Display */
            <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              {/* Basic Information */}
              <div className="sm:col-span-2 border-b border-indigo-100 pb-4">
                <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Basic Information</h4>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Vendor Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{vendor.name}</dd>
              </div>

              {vendor.dba && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <dt className="text-sm font-medium text-gray-500">DBA</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vendor.dba}</dd>
                </div>
              )}

              {/* Vendor Type - Display */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Vendor Type</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {loadingVendorTypes ? (
                    <span className="text-gray-500">Loading...</span>
                  ) : currentVendorType ? (
                    <div>
                      <span className="font-medium">{currentVendorType.name}</span>
                      {currentVendorType.description && (
                        <p className="text-gray-500 text-xs mt-1">{currentVendorType.description}</p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500">No vendor type selected</span>
                  )}
                </dd>
              </div>

              {vendor.phone && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a href={`tel:${vendor.phone}`} className="text-indigo-600 hover:text-indigo-900">
                      {vendor.phone}
                    </a>
                  </dd>
                </div>
              )}

              {vendor.website && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <dt className="text-sm font-medium text-gray-500">Website</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">
                      {vendor.website}
                    </a>
                  </dd>
                </div>
              )}

              {vendor.description && (
                <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{vendor.description}</dd>
                </div>
              )}

              {/* Address Information */}
              {(vendor.address_line1 || vendor.city || vendor.state || vendor.zip_code) && (
                <>
                  <div className="sm:col-span-2 border-b border-indigo-100 pb-4 mt-6">
                    <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Address Information</h4>
                  </div>
                  
                  <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <dt className="text-sm font-medium text-gray-500">Address</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {vendor.address_line1 && <div>{vendor.address_line1}</div>}
                      {vendor.address_line2 && <div>{vendor.address_line2}</div>}
                      {(vendor.city || vendor.state || vendor.zip_code) && (
                        <div>
                          {vendor.city && vendor.city}
                          {vendor.city && vendor.state && ', '}
                          {vendor.state && vendor.state}
                          {vendor.zip_code && ` ${vendor.zip_code}`}
                        </div>
                      )}
                    </dd>
                  </div>
                </>
              )}

              {/* Additional Information */}
              <div className="sm:col-span-2 border-b border-indigo-100 pb-4 mt-6">
                <h4 className="text-lg leading-6 font-medium text-indigo-700 mb-4">Additional Information</h4>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    vendor.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Preferred Vendor</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {vendor.is_preferred ? 'Yes' : 'No'}
                </dd>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : 'N/A'}
                </dd>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {vendor.updated_at ? new Date(vendor.updated_at).toLocaleDateString() : 'N/A'}
                </dd>
              </div>

              {vendor.notes && (
                <div className="sm:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{vendor.notes}</dd>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorInfoTab; 